import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { Observable, Subject } from 'rxjs';
import { takeUntil, finalize } from 'rxjs/operators';

// Services
import { AuthService } from '../../services/auth.service';
import { PreAlertService } from '../../services/pre-alert.service';

// Models
import { PreAlert } from '../../../models/pre-alert.models';

interface ManifestData {
  registryNumber: string;
  billOfLadingReference: string; // Renamed from houseAirwaybill
  masterAwb: string;
  blType: 'house' | 'master';
  nature: string;
  customsOfficeCode: string;
  dateOfArrival: string; // New field
  timeOfArrival: string; // New field
  blNumber: string;
  placeOfLoading: string;
  placeOfUnloading: string;
  transportMode: string;
  grossWeight: string; // New field
  exporterShipper: string;
  exporterAddress: string;
  carrierCode: string; // New field
  nationalityCode: string; // New field
  nationalityName: string; // New field
  transportIdentity: string; // Renamed from transport
  consignee: string;
  consigneeAddress: string;
  numberOfPackages: number; // New field
  packagesCodes: string;
  marksAndNumbers: string;
  manifestedPackages: number;
  manifestedGrossMass: number;
  descriptionOfGoods: string;
}

interface XmlTemplate {
  id: string;
  name: string;
  description: string;
  provider: string;
  xmlContent: string;
  fieldMappings: Record<string, string>;
  createdAt: Date;
  updatedAt: Date;
}

@Component({
  selector: 'app-manifest',
  imports: [CommonModule, FormsModule, ReactiveFormsModule],
  templateUrl: './manifest.component.html',
  styleUrl: './manifest.component.css'
})
export class ManifestComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();

  // Auth observables
  isAdmin$: Observable<boolean>;
  isShopeeDocsTeam$: Observable<boolean>;

  // Form
  manifestForm: FormGroup;

  // Data
  preAlerts: PreAlert[] = [];
  selectedPreAlert: PreAlert | null = null;
  manifestData: ManifestData | null = null;

  // XML Template system
  xmlTemplates: XmlTemplate[] = [];
  selectedTemplate: XmlTemplate | null = null;
  xmlPreview = '';

  // Loading states
  isLoading = false;
  isSubmitting = false;
  isLoadingTemplates = false;
  isGeneratingXml = false;

  // Messages
  successMessage = '';
  errorMessage = '';

  // BL Type options
  blTypeOptions = [
    { value: 'house', label: 'House Airwaybill' },
    { value: 'master', label: 'Master Airwaybill' }
  ];

  // Customs Office options
  customsOfficeOptions = [
    { code: 'P03', name: 'NINOY AQUINO INTL AIRPORT' },
    { code: 'P07B', name: 'SUB-PORT OF MACTAN' },
    { code: 'P12AB', name: 'DAVAO AIRPORT' },
    { code: 'P14', name: 'PORT OF CLARK' }
  ];

  // Carrier options
  carrierOptions = [
    { 
      code: 'DUM', 
      name: 'DUMMY MANIFEST', 
      address: 'BUREAU OF CUSTOMS\n\nMANILA 99999\nPHILIPPINES' 
    },
    { 
      code: 'NAI', 
      name: 'NAIA DUMMY MANIFEST', 
      address: 'BUREAU OF CUSTOMS\nDUMMY MANIFEST\nMANILA 99999\nPHILIPPINES' 
    }
  ];

  constructor(
    private authService: AuthService,
    private preAlertService: PreAlertService,
    private formBuilder: FormBuilder,
    private router: Router
  ) {
    // Initialize auth observables
    this.isAdmin$ = this.authService.isAdmin$;
    this.isShopeeDocsTeam$ = this.authService.isShopeeDocsTeam$;

    // Initialize form
    this.manifestForm = this.createManifestForm();
  }

  ngOnInit(): void {
    this.loadPreAlerts();
    this.loadTemplates();
    this.initializeDropdownOptions();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Initialize dropdown options for form
   */
  private initializeDropdownOptions() {
    // Initialize customs office options
    this.customsOfficeOptions = [
      { code: 'P03', name: 'NINOY AQUINO INTL AIRPORT' },
      { code: 'P07B', name: 'SUB-PORT OF MACTAN' },
      { code: 'P12AB', name: 'DAVAO AIRPORT' },
      { code: 'P14', name: 'PORT OF CLARK' }
      // Add more customs offices as needed
    ];

    // Initialize carrier options
    this.carrierOptions = [
      { code: 'NAI', name: 'NAIA DUMMY MANIFEST', address: 'BUREAU OF CUSTOMS DUMMY MANIFEST MANILA 99999 PHILIPPINES' },
      { code: 'DUM', name: 'DUMMY MANIFEST', address: 'BUREAU OF CUSTOMS DUMMY MANIFEST MANILA 99999 PHILIPPINES' },
      // Add more carriers as needed
    ];
  }

  /**
   * Create manifest form with validation
   */
  private createManifestForm(): FormGroup {
    return this.formBuilder.group({
      preAlertId: ['', Validators.required],
      registryNumber: ['', Validators.required],
      billOfLadingReference: ['', Validators.required], // Renamed from houseAirwaybill
      masterAwb: [''], // New field
      blType: ['house', Validators.required],
      nature: ['', Validators.required],
      customsOfficeCode: ['', Validators.required],
      dateOfArrival: ['', Validators.required], // New field
      timeOfArrival: ['', Validators.required], // New field
      blNumber: ['1'], // New field with default value
      placeOfLoading: [''], // New field (auto-populated from pre-alert)
      placeOfUnloading: [''], // New field (auto-populated from pre-alert)
      transportMode: ['4'], // New field with default value "4"
      grossWeight: [''], // New field for Total_gross_mass_manifested
      exporterShipper: ['', Validators.required],
      exporterAddress: [''], // New field
      carrierCode: ['DUM', Validators.required], // New field for carrier dropdown
      nationalityCode: ['', Validators.required], // New field
      nationalityName: ['', Validators.required], // New field
      transportIdentity: [''], // New field (renamed from transport)
      consignee: ['', Validators.required],
      consigneeAddress: [''], // New field (Csnee Address)
      numberOfPackages: [''], // New field
      packagesCodes: ['', Validators.required],
      marksAndNumbers: ['', Validators.required],
      descriptionOfGoods: ['', Validators.required]
    });
  }

  /**
   * Load available pre-alerts
   */
  loadPreAlerts(): void {
    this.isLoading = true;
    
    this.preAlertService.getPreAlerts({
      page: 1,
      page_size: 1000,
      status: ''
    }).pipe(
      takeUntil(this.destroy$),
      finalize(() => this.isLoading = false)
    ).subscribe({
      next: (response) => {
        this.preAlerts = response.items || [];
      },
      error: (error) => {
        console.error('Error loading pre-alerts:', error);
        this.errorMessage = 'Failed to load pre-alerts. Please refresh the page.';
        this.preAlerts = [];
      }
    });
  }

  /**
   * Handle pre-alert selection
   */
  onPreAlertSelected(): void {
    const preAlertId = this.manifestForm.get('preAlertId')?.value;
    this.selectedPreAlert = this.preAlerts.find(pa => pa.ladingBill === preAlertId) || null;
    
    if (this.selectedPreAlert) {
      this.populateFormFromPreAlert();
    }
  }

  /**
   * Populate form fields from selected pre-alert data
   */
  private populateFormFromPreAlert(): void {
    if (!this.selectedPreAlert) return;

    // Auto-populate fields from pre-alert data
    this.manifestForm.patchValue({
      // Auto-populate port fields from pre-alert departure/arrival locations
      placeOfLoading: this.selectedPreAlert.departureLocation || '',
      placeOfUnloading: this.selectedPreAlert.arrivalLocation || '',
      // Auto-populate date and time from ETA
      dateOfArrival: this.selectedPreAlert.eta ? new Date(this.selectedPreAlert.eta).toISOString().split('T')[0] : '',
      timeOfArrival: this.selectedPreAlert.eta ? new Date(this.selectedPreAlert.eta).toTimeString().split(' ')[0] : '',
      // Auto-populate weight and packages
      grossWeight: this.selectedPreAlert.chargeableWeight?.toString() || '',
      numberOfPackages: this.selectedPreAlert.pieces || '',
      // Keep the default values for other fields
      blNumber: '1',
      transportMode: '4',
      carrierCode: 'DUM'
    });

    // Show success message
    this.successMessage = 'Pre-alert data loaded successfully. Port fields, arrival date/time, weight, and packages have been auto-populated.';
    setTimeout(() => this.successMessage = '', 3000);
  }

  /**
   * Generate manifest data
   */
  generateManifest(): void {
    if (this.manifestForm.invalid) {
      this.markFormGroupTouched();
      this.errorMessage = 'Please fill in all required fields.';
      return;
    }

    this.isSubmitting = true;
    const formData = this.manifestForm.value;

    // Create manifest data object
    this.manifestData = {
      registryNumber: formData.registryNumber,
      billOfLadingReference: formData.billOfLadingReference,
      masterAwb: formData.masterAwb || '',
      blType: formData.blType,
      nature: formData.nature,
      customsOfficeCode: formData.customsOfficeCode,
      dateOfArrival: formData.dateOfArrival,
      timeOfArrival: formData.timeOfArrival,
      blNumber: formData.blNumber || '1',
      placeOfLoading: this.selectedPreAlert?.departureLocation || formData.placeOfLoading || '',
      placeOfUnloading: this.selectedPreAlert?.arrivalLocation || formData.placeOfUnloading || '',
      transportMode: formData.transportMode || '4',
      grossWeight: formData.grossWeight || this.selectedPreAlert?.chargeableWeight?.toString() || '0',
      exporterShipper: formData.exporterShipper,
      exporterAddress: formData.exporterAddress || '',
      carrierCode: formData.carrierCode,
      nationalityCode: formData.nationalityCode,
      nationalityName: formData.nationalityName,
      transportIdentity: formData.transportIdentity || '',
      consignee: formData.consignee,
      consigneeAddress: formData.consigneeAddress || '',
      numberOfPackages: formData.numberOfPackages || this.selectedPreAlert?.pieces || 0,
      packagesCodes: formData.packagesCodes,
      marksAndNumbers: formData.marksAndNumbers,
      manifestedPackages: this.selectedPreAlert?.pieces || 0,
      manifestedGrossMass: this.selectedPreAlert?.chargeableWeight || 0,
      descriptionOfGoods: formData.descriptionOfGoods
    };

    setTimeout(() => {
      this.isSubmitting = false;
      this.successMessage = 'Manifest generated successfully! You can now download the XML file.';
      this.updateXmlPreview();
    }, 1000);
  }

  /**
   * Generate and download XML file
   */
  generateXmlFile(): void {
    if (!this.manifestData) {
      this.errorMessage = 'Please generate manifest data first.';
      return;
    }

    if (!this.selectedTemplate) {
      this.errorMessage = 'Please select an XML template.';
      return;
    }

    this.isGeneratingXml = true;

    // Generate XML content using selected template
    const xmlContent = this.createXmlFromTemplate(this.manifestData, this.selectedTemplate);

    // Create and download file
    setTimeout(() => {
      this.downloadXmlFile(xmlContent);
      this.isGeneratingXml = false;
      this.successMessage = 'XML file downloaded successfully!';
    }, 1000);
  }

  /**
   * Load templates from localStorage or initialize defaults
   */
  loadTemplates(): void {
    this.isLoadingTemplates = true;
    
    // Clear localStorage to remove any duplicate templates
    localStorage.removeItem('xmlTemplates');
    
    // Always initialize with the latest default template
    this.initializeDefaultTemplates();
    
    this.isLoadingTemplates = false;
    this.updateXmlPreview();
  }

  /**
   * Initialize default templates
   */
  private initializeDefaultTemplates(): void {
    const ekonekTemplate: XmlTemplate = {
      id: 'ekonek-air-manifest',
      name: 'Ekonek Air Manifest',
      description: 'Air manifest template for Ekonek customs provider',
      provider: 'Ekonek',
      xmlContent: this.getEkonekTemplate(),
      fieldMappings: this.getEkonekFieldMappings(),
      createdAt: new Date(),
      updatedAt: new Date()
    };

    this.xmlTemplates = [ekonekTemplate];
    this.selectedTemplate = ekonekTemplate;
    
    // Save to localStorage
    localStorage.setItem('xmlTemplates', JSON.stringify(this.xmlTemplates));
  }

  /**
   * Get Ekonek XML template content
   */
  private getEkonekTemplate(): string {
    return `<TWM_BOL>
   <Identification_segment>
      <Registry_number>{{registryNumber}}</Registry_number>
      <Bol_reference>{{billOfLadingReference}}</Bol_reference>
      <Customs_office_segment>
         <Code>{{customsOfficeCode}}</Code>
         <Name>{{customsOfficeName}}</Name>
      </Customs_office_segment>
   </Identification_segment>
   <Manifest_section>
      <Registraion_segment>
         <Year_of_registration/>
         <Number/>
      </Registraion_segment>
      <Arrival_segment>
         <Date_of_arrival>{{dateOfArrival}}</Date_of_arrival>
         <Time_of_arrival>{{timeOfArrival}}</Time_of_arrival>
      </Arrival_segment>
   </Manifest_section>
   <Bol_specific_segment>
      <Line_number>{{blNumber}}</Line_number>
      <Previous_document_reference>{{masterAwb}}</Previous_document_reference>
      <Bol_Nature>{{nature}}</Bol_Nature>
      <Unique_carrier_reference/>
      <Total_number_of_containers>0</Total_number_of_containers>
      <Total_gross_mass_manifested>{{grossWeight}}</Total_gross_mass_manifested>
      <Volume_in_cubic_meters/>
      <Bol_type_segment>
         <Code>HBL</Code>
         <Name>HOUSE BILL OF LADING</Name>
      </Bol_type_segment>
      <Exporter_segment>
         <Code/>
         <Name oes1-unsign-content="true">{{exporterShipper}}</Name>
         <Address oes1-unsign-content="true">{{exporterAddress}}</Address>
      </Exporter_segment>
      <Consignee_segment>
         <Code/>
         <Name oes1-unsign-content="true">{{consignee}}</Name>
         <Address oes1-unsign-content="true">{{consigneeAddress}}</Address>
      </Consignee_segment>
      <Notify_segment>
         <Code/>
         <Name oes1-unsign-content="true">{{consignee}}</Name>
         <Address oes1-unsign-content="true">{{consigneeAddress}}</Address>
      </Notify_segment>
      <Carrier_segment>
         <Code>{{carrierCode}}</Code>
         <Name>{{carrierName}}</Name>
         <Address>{{carrierAddress}}</Address>
      </Carrier_segment>
      <Place_of_loading_segment>
         <Code>{{placeOfLoading}}</Code>
      </Place_of_loading_segment>
      <Place_of_unloading_segment>
         <Code>{{placeOfUnloading}}</Code>
      </Place_of_unloading_segment>
      <Packages_segment>
         <Package_type_code>{{packagesCodes}}</Package_type_code>
         <Number_of_packages>{{numberOfPackages}}</Number_of_packages>
      </Packages_segment>
      <Shipping_segment>
         <Shipping_marks>{{marksAndNumbers}}</Shipping_marks>
      </Shipping_segment>
      <Goods_segment>
         <Goods_description>{{descriptionOfGoods}}</Goods_description>
      </Goods_segment>
      <Freight_segment>
         <Value/>
         <Currency/>
         <Indicator_segment>
            <Code/>
         </Indicator_segment>
      </Freight_segment>
      <Customs_segment>
         <Value/>
         <Currency/>
      </Customs_segment>
      <Transport_segment>
         <Value/>
         <Currency/>
         <Name_of_transporter>{{transportIdentity}}</Name_of_transporter>
         <Mode_of_transport_segment>
            <Code>4</Code>
            <Name>AIR TRANSPORT</Name>
         </Mode_of_transport_segment>
         <Nationality_of_transport_segment>
            <Code>{{nationalityCode}}</Code>
            <Name>{{nationalityName}}</Name>
         </Nationality_of_transport_segment>
      </Transport_segment>
      <Insurance_segment>
         <Value/>
         <Currency/>
      </Insurance_segment>
      <Seals_segment>
         <Number_of_seals/>
         <Marks_of_seals/>
         <Sealing_party_code/>
      </Seals_segment>
      <Information_segment>
         <Information_part_a/>
      </Information_segment>
      <Operations_segment>
         <Location_segment>
            <Code/>
            <Information/>
         </Location_segment>
         <Onward_transport_segment>
            <Transit_segment>
               <Customs_office_code/>
               <Customs_office_name/>
               <Document_reference/>
            </Transit_segment>
            <Transhipment_segment>
               <Transipment_location_code/>
               <Transhipment_location_name/>
               <Document_reference/>
            </Transhipment_segment>
            <Onward_carrier_segment>
               <Code/>
               <Name/>
            </Onward_carrier_segment>
         </Onward_transport_segment>
      </Operations_segment>
   </Bol_specific_segment>
</TWM_BOL>`;
  }

  /**
   * Get Ekonek field mappings
   */
  private getEkonekFieldMappings(): Record<string, string> {
    return {
      // Direct mappings from form fields
      'registryNumber': 'registryNumber',
      'billOfLadingReference': 'billOfLadingReference',
      'customsOfficeCode': 'customsOfficeCode',
      'dateOfArrival': 'dateOfArrival',
      'timeOfArrival': 'timeOfArrival',
      'blNumber': 'blNumber',
      'masterAwb': 'masterAwb',
      'nature': 'nature',
      'grossWeight': 'grossWeight',
      'exporterShipper': 'exporterShipper',
      'exporterAddress': 'exporterAddress',
      'consignee': 'consignee',
      'consigneeAddress': 'consigneeAddress',
      'carrierCode': 'carrierCode',
      'placeOfLoading': 'placeOfLoading',
      'placeOfUnloading': 'placeOfUnloading',
      'packagesCodes': 'packagesCodes',
      'numberOfPackages': 'numberOfPackages',
      'marksAndNumbers': 'marksAndNumbers',
      'descriptionOfGoods': 'descriptionOfGoods',
      'transportIdentity': 'transportIdentity',
      'nationalityCode': 'nationalityCode',
      'nationalityName': 'nationalityName',
      
      // Dynamic mappings based on customs office code
      'customsOfficeName': 'dynamic:customsOffice',
      
      // Dynamic mappings based on carrier code
      'carrierName': 'dynamic:carrierName',
      'carrierAddress': 'dynamic:carrierAddress'
    };
  }

  /**
   * Create XML from template with field mappings
   */
  private createXmlFromTemplate(data: ManifestData, template: XmlTemplate): string {
    let xmlContent = template.xmlContent;
    
    // Apply field mappings
    Object.entries(template.fieldMappings).forEach(([xmlField, formField]) => {
      const placeholder = `{{${xmlField}}}`;
      let value = '';
      
      if (formField === 'dynamic:customsOffice') {
        // Get customs office name based on code
        const office = this.customsOfficeOptions.find(o => o.code === data.customsOfficeCode);
        value = office ? office.name : '';
      } else if (formField === 'dynamic:carrierName') {
        // Get carrier name based on code
        const carrier = this.carrierOptions.find(c => c.code === data.carrierCode);
        value = carrier ? carrier.name : '';
      } else if (formField === 'dynamic:carrierAddress') {
        // Get carrier address based on code
        const carrier = this.carrierOptions.find(c => c.code === data.carrierCode);
        value = carrier ? carrier.address : '';
      } else if (formField.startsWith('static:')) {
        // Static value
        value = formField.replace('static:', '');
      } else {
        // Direct field mapping
        value = (data as any)[formField] || '';
      }
      
      xmlContent = xmlContent.replace(new RegExp(placeholder, 'g'), value);
    });
    
    return xmlContent;
  }

  /**
   * Update XML preview
   */
  updateXmlPreview(): void {
    if (this.manifestData && this.selectedTemplate) {
      this.xmlPreview = this.createXmlFromTemplate(this.manifestData, this.selectedTemplate);
    } else {
      this.xmlPreview = '';
    }
  }

  /**
   * Handle template selection change
   */
  onTemplateChange(event: any): void {
    const templateId = event.target.value;
    if (templateId) {
      this.selectedTemplate = this.xmlTemplates.find(t => t.id === templateId) || null;
    } else {
      this.selectedTemplate = null;
    }
    this.updateXmlPreview();
  }

  /**
   * Create basic XML from manifest data
   */
  private createBasicXmlFromData(data: ManifestData): string {
    const arrivalDate = data.dateOfArrival;
    const arrivalTime = data.timeOfArrival;

    return `<TWM_BOL>
   <Identification_segment>
      <Registry_number>${data.registryNumber}</Registry_number>
      <Bol_reference>${data.billOfLadingReference}</Bol_reference>
      <Customs_office_segment>
         <Code>${data.customsOfficeCode}</Code>
      </Customs_office_segment>
   </Identification_segment>
   <Manifest_section>
      <Arrival_segment>
         <Date_of_arrival>${arrivalDate}</Date_of_arrival>
         <Time_of_arrival>${arrivalTime}</Time_of_arrival>
      </Arrival_segment>
   </Manifest_section>
   <Bol_specific_segment>
      <Line_number>${data.blNumber}</Line_number>
      <Previous_document_reference>${data.masterAwb}</Previous_document_reference>
      <Bol_Nature>${data.nature}</Bol_Nature>
      <Total_gross_mass_manifested>${data.grossWeight}</Total_gross_mass_manifested>
      <Marks_and_numbers>${data.marksAndNumbers}</Marks_and_numbers>
      <Country_of_origin_of_the_goods>CN</Country_of_origin_of_the_goods>
      <Total_packages>${data.numberOfPackages}</Total_packages>
      <Description_of_goods>${data.descriptionOfGoods}</Description_of_goods>
      <Transport_segment>
         <Means_of_transport_name>${data.transportIdentity}</Means_of_transport_name>
         <Nationality>${data.nationalityName}</Nationality>
      </Transport_segment>
      <Commercial_segments>
         <Exporter_shipper>${data.exporterShipper}</Exporter_shipper>
         <Consignee>${data.consignee}</Consignee>
         <Notify>${data.consignee}</Notify>
      </Commercial_segments>
   </Bol_specific_segment>
</TWM_BOL>`;
  }

  /**
   * Download XML file
   */
  private downloadXmlFile(content: string): void {
    const blob = new Blob([content], { type: 'application/xml' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    
    link.href = url;
    link.download = `manifest_${this.manifestData?.registryNumber || 'generated'}_${new Date().getTime()}.xml`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  }

  /**
   * Mark all form fields as touched to show validation errors
   */
  private markFormGroupTouched(): void {
    Object.keys(this.manifestForm.controls).forEach(key => {
      const control = this.manifestForm.get(key);
      control?.markAsTouched();
    });
  }

  /**
   * Reset form and data
   */
  resetForm(): void {
    this.manifestForm.reset();
    this.manifestForm.patchValue({ blType: 'house' });
    this.selectedPreAlert = null;
    this.manifestData = null;
    this.clearMessages();
  }

  /**
   * Clear status messages
   */
  clearMessages(): void {
    this.successMessage = '';
    this.errorMessage = '';
  }

  /**
   * Check if field has error and is touched
   */
  hasFieldError(fieldName: string): boolean {
    const field = this.manifestForm.get(fieldName);
    return !!(field && field.invalid && field.touched);
  }

  /**
   * Get field error message
   */
  getFieldError(fieldName: string): string {
    const field = this.manifestForm.get(fieldName);
    if (field?.errors?.['required']) {
      return `${fieldName.charAt(0).toUpperCase() + fieldName.slice(1)} is required`;
    }
    return '';
  }
}




